/**
 * studio-shop D4 — the shop's bag, kept in this browser for the studio's
 * address (`localStorage` is per address): option ids and quantities, at most
 * 20 lines, each 1 to 10 of one option, and the unit price the bag last showed
 * so a change can be marked. The server prices it (`POST /shop/bag/price`);
 * nothing here is a price the buyer pays. Every tab and every piece on the page
 * reads the same bag.
 *
 * site-form-buy-mode D3: a line a website form claimed carries the answers held for it, and stays at one.
 */
import { useSyncExternalStore } from 'react';

export const BAG_MAX_LINES = 20;
export const BAG_MAX_QUANTITY = 10;

/** The answers a website form held for the line: their id, and the key that proves them at checkout. */
export interface HeldAnswersRef {
  readonly id: string;
  readonly key: string;
}

export interface BagLine {
  readonly optionId: string;
  readonly quantity: number;
  /** The unit price the bag last showed for it. */
  readonly price?: string;
  /** site-form-buy-mode: the answers held for this line; such a line holds one. */
  readonly answers?: HeldAnswersRef;
}

const clamp = (quantity: number) => Math.min(BAG_MAX_QUANTITY, Math.max(1, Math.floor(quantity)));
const PRICE = /^\d+\.\d{2}$/;

function heldAnswers(value: unknown): HeldAnswersRef | null {
  const { id, key } = (value ?? {}) as { id?: unknown; key?: unknown };
  if (typeof id !== 'string' || !id || id.length > 64 || typeof key !== 'string' || !key || key.length > 64) return null;
  return { id, key };
}

/** One more of the option: its line grows (up to 10), or a new line joins a bag of fewer than 20; `price` is the one the buyer was shown. */
export function addToBag(bag: readonly BagLine[], optionId: string, price?: string): { bag: readonly BagLine[]; added: boolean } {
  const shown = price ? { price } : {};
  const at = bag.findIndex((line) => line.optionId === optionId);
  if (at >= 0) {
    if (bag[at]!.answers || bag[at]!.quantity >= BAG_MAX_QUANTITY) return { bag, added: false };
    return { bag: bag.map((line, i) => (i === at ? { ...line, quantity: line.quantity + 1, ...shown } : line)), added: true };
  }
  if (bag.length >= BAG_MAX_LINES) return { bag, added: false };
  return { bag: [...bag, { optionId, quantity: 1, ...shown }], added: true };
}

/**
 * site-form-buy-mode D3: the option claimed by a website form, one of it carrying the answers held for it. A second
 * claim replaces the line, and the answers with it; a new line joins a bag of fewer than 20.
 */
export function claimInBag(
  bag: readonly BagLine[],
  optionId: string,
  answers: HeldAnswersRef,
  price?: string,
): { bag: readonly BagLine[]; added: boolean } {
  const line: BagLine = { optionId, quantity: 1, ...(price ? { price } : {}), answers };
  const at = bag.findIndex((existing) => existing.optionId === optionId);
  if (at >= 0) return { bag: bag.map((existing, i) => (i === at ? line : existing)), added: true };
  if (bag.length >= BAG_MAX_LINES) return { bag, added: false };
  return { bag: [...bag, line], added: true };
}

/** The line's quantity, kept between 1 and 10; 0 or less takes the line out. A claimed line stays at one. */
export function setQuantity(bag: readonly BagLine[], optionId: string, quantity: number): readonly BagLine[] {
  if (quantity < 1) return removeFromBag(bag, optionId);
  return bag.map((line) => (line.optionId === optionId && !line.answers ? { ...line, quantity: clamp(quantity) } : line));
}

export function removeFromBag(bag: readonly BagLine[], optionId: string): readonly BagLine[] {
  return bag.filter((line) => line.optionId !== optionId);
}

/** Keeps the price each line showed (none for a line no longer sold). */
export function rememberPrices(bag: readonly BagLine[], priced: readonly { optionId: string; unitPrice: string | null }[]): readonly BagLine[] {
  const prices = new Map(priced.map((line) => [line.optionId, line.unitPrice]));
  return bag.map(({ optionId, quantity, answers }) => {
    const price = prices.get(optionId);
    return { optionId, quantity, ...(price ? { price } : {}), ...(answers ? { answers } : {}) };
  });
}

/** The stored bag, read defensively: the first 20 good lines, one per option, quantities clamped. */
export function parseBag(raw: string | null): readonly BagLine[] {
  let value: unknown;
  try {
    value = raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const bag: BagLine[] = [];
  for (const item of value) {
    const { optionId, quantity, price, answers } = (item ?? {}) as { optionId?: unknown; quantity?: unknown; price?: unknown; answers?: unknown };
    if (typeof optionId !== 'string' || !optionId || optionId.length > 64 || seen.has(optionId)) continue;
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity < 1) continue;
    const held = answers === undefined ? null : heldAnswers(answers);
    // A claimed line whose answers cannot be read goes, rather than be bought without them.
    if (answers !== undefined && !held) continue;
    seen.add(optionId);
    bag.push({
      optionId,
      quantity: held ? 1 : clamp(quantity),
      ...(typeof price === 'string' && PRICE.test(price) ? { price } : {}),
      ...(held ? { answers: held } : {}),
    });
    if (bag.length === BAG_MAX_LINES) break;
  }
  return bag;
}

// ---------------------------------------------------------------- the bag in this browser

const STORAGE_KEY = 'telify:shop-bag';
const EMPTY: readonly BagLine[] = [];
let cached: { raw: string | null; bag: readonly BagLine[] } | null = null;
const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function snapshot(): readonly BagLine[] {
  const raw = readStored();
  if (!cached || cached.raw !== raw) cached = { raw, bag: raw ? parseBag(raw) : EMPTY };
  return cached.bag;
}

function write(bag: readonly BagLine[]): void {
  try {
    if (bag.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(bag));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode): the bag lasts this page only */
    cached = { raw: null, bag };
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  // Another tab changed the bag.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) notify();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(notify);
    window.removeEventListener('storage', onStorage);
  };
}

export interface Bag {
  readonly lines: readonly BagLine[];
  /** How many items, every line's quantity added up. */
  readonly count: number;
  /** One more of the option, at the price shown; false when its line already holds 10 or the bag already has 20 lines. */
  readonly add: (optionId: string, price?: string) => boolean;
  /** site-form-buy-mode: one of the option, carrying the answers a website form held for it; false when the bag already has 20 lines. */
  readonly claim: (optionId: string, answers: HeldAnswersRef, price?: string) => boolean;
  readonly setQuantity: (optionId: string, quantity: number) => void;
  readonly remove: (optionId: string) => void;
  /** Keeps the prices the bag just showed. */
  readonly rememberPrices: (priced: readonly { optionId: string; unitPrice: string | null }[]) => void;
  /** Empties the bag, once its order is paid for. */
  readonly clear: () => void;
}

export function useBag(): Bag {
  const lines = useSyncExternalStore(subscribe, snapshot, () => EMPTY);
  return {
    lines,
    count: lines.reduce((sum, line) => sum + line.quantity, 0),
    add: (optionId, price) => {
      const next = addToBag(snapshot(), optionId, price);
      if (next.added) write(next.bag);
      return next.added;
    },
    claim: (optionId, answers, price) => {
      const next = claimInBag(snapshot(), optionId, answers, price);
      if (next.added) write(next.bag);
      return next.added;
    },
    setQuantity: (optionId, quantity) => write(setQuantity(snapshot(), optionId, quantity)),
    remove: (optionId) => write(removeFromBag(snapshot(), optionId)),
    rememberPrices: (priced) => {
      const current = snapshot();
      const next = rememberPrices(current, priced);
      if (JSON.stringify(next) !== JSON.stringify(current)) write(next);
    },
    clear: () => write(EMPTY),
  };
}
